import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { post } from '../utils/apiClient';
import { nowISO } from '../utils/dateUtils';
import { logger } from "../utils/logger";
import { getErrorTrailSnapshot, errorTrailLength } from '../utils/errorTrail';

/**
 * Scroll-hide via direct DOM style — avoids React re-renders on every
 * scroll direction change.  Returns a ref to attach to the container.
 */
function useScrollHide(pathname: string) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastScrollY = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const isOpenRef = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    }
    lastScrollY.current = 0;

    const show = () => {
      if (!containerRef.current) return;
      containerRef.current.style.opacity = '1';
      containerRef.current.style.transform = 'translateY(0)';
    };
    const hide = () => {
      if (!containerRef.current || isOpenRef.current) return;
      containerRef.current.style.opacity = '0';
      containerRef.current.style.transform = 'translateY(20px)';
    };

    const handleScroll = (e: Event) => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const target = e.target;
        const currentY = target === document ? window.scrollY : ((target as HTMLElement).scrollTop || 0);
        if (currentY > lastScrollY.current + 10) {
          hide();
        } else if (currentY < lastScrollY.current - 10) {
          show();
        }
        lastScrollY.current = currentY;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(show, 2000);
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    const main = document.querySelector('main');
    if (main) main.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (main) main.removeEventListener('scroll', handleScroll);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [pathname]);

  return { containerRef, isOpenRef };
}

const MAX_CLICK_TRAIL = 20;

function useClickTrail() {
  const trailRef = useRef([]);

  useEffect(() => {
    const handler = (e) => {
      const target = e.target;
      if (!target) return;
      const entry = {
        tag: target.tagName?.toLowerCase(),
        text: (target.textContent || target.ariaLabel || '').slice(0, 80),
        id: target.id || undefined,
        className: (target.className && typeof target.className === 'string')
          ? target.className.split(' ').slice(0, 3).join(' ')
          : undefined,
        ts: Date.now(),
      };
      const trail = trailRef.current;
      trail.push(entry);
      if (trail.length > MAX_CLICK_TRAIL) trail.shift();
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, []);

  return trailRef;
}

const TYPES = [
  { value: 'Bug', label: 'Bug Report', icon: BugIcon, color: 'text-red-500' },
  { value: 'Suggestion', label: 'Suggestion', icon: LightbulbIcon, color: 'text-amber-500' },
  { value: 'Praise', label: 'Compliment', icon: HeartIcon, color: 'text-pink-500' },
];

const HIDDEN_PATHS = ['/ai-assistant', '/voice', '/calendar'];

const FeedbackButton = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('');
  const [userGoal, setUserGoal] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();
  const clickTrailRef = useClickTrail();
  const modalRef = useRef(null);
  const { containerRef, isOpenRef } = useScrollHide(location.pathname);

  useEffect(() => {
    isOpenRef.current = isOpen;
    if (isOpen && containerRef.current) {
      containerRef.current.style.opacity = '1';
      containerRef.current.style.transform = 'translateY(0)';
    }
  }, [isOpen, isOpenRef, containerRef]);

  const getScreenDimensions = useCallback(
    () => `${window.innerWidth}x${window.innerHeight}`,
    [],
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error('Please describe the issue or feedback');
      return;
    }
    if (!feedbackType) {
      toast.error('Please select a feedback type');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await post('/api/feedback', {
        feedback_type: feedbackType,
        message,
        user_goal: userGoal || null,
        current_url: location.pathname + location.search,
        screen_dimensions: getScreenDimensions(),
        click_trail: clickTrailRef.current.slice(),
        console_errors: errorTrailLength() > 0 ? getErrorTrailSnapshot() : null,
        metadata_extra: {
          referrer: document.referrer || null,
          timestamp_local: nowISO(),
        },
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      await res.json();
      setMessage('');
      setUserGoal('');
      setFeedbackType('');
      setIsOpen(false);
      toast.success('Thank you for your feedback!');
    } catch (err) {
      logger.error('Feedback submit error:', err);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      setIsOpen(false);
    }
  };

  if (HIDDEN_PATHS.some(p => location.pathname.startsWith(p))) return null;

  return (
    <div ref={containerRef}
         className="fixed bottom-20 sm:bottom-4 right-2 z-[55] pointer-events-none"
         style={{
           paddingBottom: 'env(safe-area-inset-bottom, 0px)',
           transition: 'opacity 0.3s ease, transform 0.3s ease',
         }}>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="pointer-events-auto m-2 flex items-center justify-center gap-1
                     h-11 w-11 sm:h-auto sm:w-auto
                     sm:gap-1.5 sm:px-3 sm:py-2
                     bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm font-medium
                     rounded-full shadow-lg transition-all duration-200
                     hover:shadow-xl hover:scale-105 active:scale-95
                     dark:bg-indigo-500 dark:hover:bg-indigo-600"
          aria-label="Send feedback or report a bug"
          title="Feedback & Bug Report"
        >
          <MegaphoneIcon />
          <span className="hidden sm:inline">Feedback</span>
        </button>
      )}

      {isOpen && createPortal(
        <div
          className="fixed inset-0 bg-black/40 flex items-end sm:items-center
                     justify-center z-[1400]"
          onClick={handleBackdropClick}
          data-interactive="true"
        >
          <div
            ref={modalRef}
            className="bg-white dark:bg-gray-800 w-full sm:max-w-md sm:rounded-xl
                       rounded-t-xl shadow-2xl overflow-hidden
                       animate-[slideUp_200ms_ease-out]"
            data-interactive="true"
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Send Feedback
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700
                           text-gray-500 dark:text-gray-400"
                aria-label="Close feedback form"
              >
                <XIcon />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-5 pb-5">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Page: <span className="font-mono">{location.pathname}</span>
              </p>

              <div className="flex gap-2 mb-4">
                {TYPES.map((t) => {
                  const Icon = t.icon;
                  const selected = feedbackType === t.value;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setFeedbackType(t.value)}
                      className={`flex-1 flex flex-col items-center gap-1 p-2.5 rounded-lg
                                  border-2 transition-all text-sm font-medium
                                  ${selected
                                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 dark:border-indigo-400'
                                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                                  }`}
                    >
                      <Icon className={selected ? 'text-indigo-600 dark:text-indigo-400' : t.color} />
                      <span className={selected
                        ? 'text-indigo-700 dark:text-indigo-300'
                        : 'text-gray-600 dark:text-gray-300'
                      }>
                        {t.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              <input
                type="text"
                className="w-full p-3 mb-3 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                           placeholder-gray-400 dark:placeholder-gray-500
                           focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                           text-sm"
                value={userGoal}
                onChange={(e) => setUserGoal(e.target.value)}
                placeholder="What were you trying to accomplish? (optional)"
              />

              <textarea
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                           placeholder-gray-400 dark:placeholder-gray-500
                           focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                           resize-none text-sm"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  feedbackType === 'Bug'
                    ? 'What happened? What did you expect?'
                    : feedbackType === 'Suggestion'
                    ? 'What would you like to see improved?'
                    : feedbackType === 'Praise'
                    ? 'What did you enjoy? We love hearing this!'
                    : 'Select a type above, then describe...'
                }
                required
              />

              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5 mb-3">
                Your recent interactions and page info will be included to help us investigate.
              </p>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                             bg-gray-100 dark:bg-gray-700 rounded-lg
                             hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !feedbackType}
                  className="px-4 py-2 text-sm font-medium text-white
                             bg-indigo-600 hover:bg-indigo-700 rounded-lg
                             disabled:opacity-50 disabled:cursor-not-allowed
                             transition-colors dark:bg-indigo-500 dark:hover:bg-indigo-600"
                >
                  {isSubmitting ? 'Sending...' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});
FeedbackButton.displayName = 'FeedbackButton';

function MegaphoneIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
         fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
         strokeLinejoin="round">
      <path d="m3 11 18-5v12L3 13v-2z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  );
}

function BugIcon({ className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
         fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
         strokeLinejoin="round" className={className}>
      <path d="m8 2 1.88 1.88" /><path d="M14.12 3.88 16 2" />
      <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
      <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" />
      <path d="M12 20v-9" /><path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
      <path d="M6 13H2" /><path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
      <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" /><path d="M22 13h-4" />
      <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
    </svg>
  );
}

function LightbulbIcon({ className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
         fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
         strokeLinejoin="round" className={className}>
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
      <path d="M9 18h6" /><path d="M10 22h4" />
    </svg>
  );
}

function HeartIcon({ className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
         fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
         strokeLinejoin="round" className={className}>
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
         fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
         strokeLinejoin="round">
      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    </svg>
  );
}

export default FeedbackButton;
